import React from 'react';
import { MaskingTape } from './MaskingTape';

/**
 * PhotoCard — clean rectangular printed photo card with a piece of masking tape
 * pinning it to the wall. Hyper-realistic paper print + tape.
 */
export const PhotoCard = ({
  src,
  alt = '',
  title = '',
  markerColor = '#ffffff',
  tapeColor = 'cream',
  tapeRotation,
  tapeWidth,
  showTitle = true,
  showTape = true,
}) => {
  // Random-ish tape rotation derived from title hash so it's stable per card
  const hash = (title || '')
    .split('')
    .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const autoRot = ((hash % 23) - 11);
  const rot = typeof tapeRotation === 'number' ? tapeRotation : autoRot;
  const tw = tapeWidth ?? (90 + (hash % 30));

  return (
    <div className="relative w-full h-full">
      {/* Drop shadow under the paper */}
      <div
        className="absolute inset-0"
        style={{
          background: 'rgba(0,0,0,0.65)',
          filter: 'blur(14px)',
          transform: 'translate(3px, 10px) scale(0.985)',
        }}
      />

      {/* Photo card with the printed image */}
      <div
        className="relative w-full h-full overflow-hidden"
        style={{ background: '#0b0b0b' }}
      >
        <img
          src={src}
          alt={alt}
          className="absolute inset-0 w-full h-full object-contain"
          style={{
            filter: 'saturate(0.85) contrast(1.08) brightness(0.94)',
          }}
        />

        {/* Bottom gradient for caption readability */}
        <div
          className="absolute inset-x-0 bottom-0 h-2/5 pointer-events-none"
          style={{
            background:
              'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0) 100%)',
          }}
        />

        {/* Paper grain / print texture */}
        <div
          className="absolute inset-0 pointer-events-none opacity-35"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2' seed='3'/><feColorMatrix values='0 0 0 0 0.1  0 0 0 0 0.08  0 0 0 0 0.06  0 0 0 0.32 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
            mixBlendMode: 'overlay',
          }}
        />

        {/* Subtle dark inner edges for depth */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            boxShadow:
              'inset 0 0 0 1px rgba(0,0,0,0.25), inset 0 -10px 18px rgba(0,0,0,0.18)',
          }}
        />

        {/* Handwritten title at the bottom */}
        {showTitle && title && (
          <div className="absolute inset-x-0 bottom-0 px-2 pb-3 text-center pointer-events-none">
            <span
              className="font-marker text-base md:text-lg uppercase tracking-wide leading-none inline-block whitespace-nowrap"
              style={{
                color: markerColor,
                textShadow:
                  '0 2px 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.6)',
                transform: 'rotate(-3deg)',
              }}
            >
              {title}
            </span>
          </div>
        )}
      </div>

      {/* Hyper-realistic masking tape pinning the top */}
      {showTape && (
        <MaskingTape
          width={tw}
          height={24}
          rotation={rot}
          color={tapeColor}
          className="absolute pointer-events-none"
          style={{
            top: '-10px',
            left: '50%',
            marginLeft: `-${tw / 2}px`,
            zIndex: 10,
          }}
        />
      )}
    </div>
  );
};
